/**
 * Blog post share button.
 *
 * snippets/share-button.liquid is not usable on the article template: its share.js does
 * `class ShareButton extends DetailsDisclosure`, and assets/details-disclosure.js (which
 * defines that class) is never loaded there — so customElements.define threw and
 * <share-button> never upgraded. details-disclosure.js also calls
 * customElements.define('details-disclosure') and ('header-menu') unguarded, so force-loading
 * it risks clashing with this theme's rebuilt header.
 *
 * This is self-contained: Web Share API where available, clipboard copy everywhere else.
 *
 * Markup contract (sections/blog-post.liquid):
 *   [data-blog-post-share]        root, carries data-share-url + data-copied-label
 *     [data-share-button]         the button
 *     [data-share-status]         aria-live region for feedback
 */

const ROOT = '[data-blog-post-share]';
const FEEDBACK_MS = 2500;

function announce(root, message) {
  const status = root.querySelector('[data-share-status]');
  if (!status) return;

  status.textContent = message;
  window.clearTimeout(root.shareTimer);
  root.shareTimer = window.setTimeout(() => {
    status.textContent = '';
  }, FEEDBACK_MS);
}

// A button may carry its own message — the Instagram and Quora icons say "paste it into …",
// since neither platform can receive a link from the web.
function copiedLabel(root, button) {
  return (button && button.dataset.copiedLabel) || root.dataset.copiedLabel || 'Link copied';
}

function copyWithClipboardApi(root, url, button) {
  return navigator.clipboard.writeText(url).then(
    () => announce(root, copiedLabel(root, button)),
    () => announce(root, root.dataset.errorLabel || 'Could not copy link')
  );
}

function copyWithTempField(root, url, button) {
  // No clipboard API — older browsers, or a non-secure context.
  const field = document.createElement('input');
  field.value = url;
  field.setAttribute('readonly', '');
  field.style.position = 'absolute';
  field.style.left = '-9999px';
  document.body.appendChild(field);
  field.select();

  try {
    document.execCommand('copy');
    announce(root, copiedLabel(root, button));
  } catch (error) {
    announce(root, root.dataset.errorLabel || 'Could not copy link');
  }

  document.body.removeChild(field);
}

function share(root, button) {
  const url = root.dataset.shareUrl || window.location.href;

  if (navigator.share) {
    // A dismissed share sheet rejects; nothing to report.
    navigator.share({ url, title: document.title }).catch(() => {});
    return;
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    copyWithClipboardApi(root, url, button);
    return;
  }

  copyWithTempField(root, url, button);
}

function setup(root) {
  if (root.dataset.shareReady === 'true') return;

  // Delegated: the row holds the "Share" button plus any icon buttons (Instagram, Quora).
  const buttons = root.querySelectorAll('[data-share-button]');
  if (!buttons.length) return;

  root.dataset.shareReady = 'true';
  buttons.forEach((button) => {
    button.addEventListener('click', () => share(root, button));
  });
}

export default function initBlogPostShare() {
  document.querySelectorAll(ROOT).forEach(setup);
}
